/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE NOM D'UNE PERSONNE SANS NOM — UN SEUL MOT, POUR TOUT `web/`.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CDC-4` F-46 règle 2 : « **un participant anonymisé apparaît sous une désignation
 * anonyme neutre, LA MÊME POUR TOUS.** L'écran **ne dit ni la cause ni la date** : une
 * date dirait qu'un compte a été supprimé ce jour-là. »
 *
 * **CE FICHIER EXISTE PARCE QUE LA RÈGLE PORTE SUR L'UNICITÉ, PAS SUR LE MOT.** « La même
 * pour tous » n'est pas une consigne de rédaction, c'est une contrainte de surface : dix
 * écrans qui choisissent chacun son mot rendent **dix états là où le produit n'en a
 * qu'un**, et une personne qui lit « Compte supprimé » ici et « Quelqu'un » là en déduit
 * deux faits différents sur la même ligne de base. Un mot recopié dans dix fichiers n'est
 * pas le même mot : c'est dix mots qui se ressemblent aujourd'hui.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LE MOT D'AVANT DISAIT DE TROP.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « **Compte supprimé** » — le mot employé dans sept fichiers jusqu'au 20260830 — **dit
 * une cause**, et deux fois de travers. Il affirme qu'un compte a été supprimé, là où
 * l'anonymisation n'est pas une suppression : la ligne reste, sa participation reste, sa
 * structure sur ce cours reste (`MODELE-DONNEES.md`, Bloc 4, Q556 et Q557). Et il désigne
 * un **compte** là où l'écran parle d'une **personne** sur une ligne de cours.
 *
 * **« Sans nom » est une absence, jamais une cause**, et c'est déjà le mot que ce produit
 * emploie pour l'auteur d'un tracé anonymisé — « à l'anonymisation, **le tracé reste sans
 * nom** » (Q377), rendu « Tracé sans nom » par `PageRelecture`. Le vocabulaire de la
 * personne et celui du tracé se rejoignent donc au lieu de diverger.
 *
 * **Le mot ne se choisit pas ici pour autant** : les deux branches — garder l'écart, ou
 * passer à « Sans nom » sur toute la surface — sont écrites en `DECISIONS.md` D-135, et
 * c'est la seconde qui est appliquée.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE FICHIER NE RANGE RIEN DANS `shared/` DU DÉPÔT, ET C'EST LA RÈGLE DE `duree.ts`.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `shared/lib/` porte « la logique métier pure » — une règle que **les deux côtés
 * rejouent**. Le serveur ne rejoue pas celle-ci : il pose `anonymise` et rend `prenom` à
 * `null`, ce qui est le fait ; **nommer l'absence est un travail d'écran**, et il n'y a
 * pas d'écran côté serveur.
 */

/**
 * **La désignation, et il n'y en a qu'une.**
 *
 * Elle est exportée pour qu'un test puisse l'affirmer, pas pour être recopiée : les
 * écrans passent par `nomAffiche`.
 */
export const DESIGNATION_ANONYME = 'Sans nom';

/**
 * **Le nom qu'un écran affiche pour une personne, quel que soit ce que le serveur rend.**
 *
 * Trois entrées donnent la désignation, et les trois sont le même fait vu de trois côtés :
 *
 * - `anonymise` vrai — le serveur l'a dit explicitement, et il peut le dire **en rendant
 *   quand même un prénom** : c'est le cas de `GET /api/v1/historique`, dont la ligne porte
 *   les deux champs. Le drapeau gagne, sans quoi le prénom d'une personne anonymisée
 *   resterait à l'écran ;
 * - `prenom` à `null` — la forme la plus courante, le serveur ayant déjà retiré le nom ;
 * - `prenom` **vide** — la forme que la table d'auteurs d'une scène emploie : elle « garde
 *   la clé avec une valeur vide, ce qui distingue *anonymisé* de *pas dans cette scène* »
 *   (D-135). Sans cette branche, l'écran rendrait une chaîne vide, donc **un blanc qui se
 *   lit comme un défaut d'affichage** plutôt que comme une absence de nom.
 *
 * Le prénom n'est **jamais** mis en forme au passage — ni coupé, ni capitalisé. « Le
 * produit ne montre jamais d'un tiers plus que son prénom » borne ce qui sort ; il
 * n'autorise pas à retoucher ce qui sort.
 */
export function nomAffiche(
  prenom: string | null | undefined,
  anonymise = false,
): string {
  if (anonymise) return DESIGNATION_ANONYME;
  if (prenom == null || prenom.trim() === '') return DESIGNATION_ANONYME;
  return prenom;
}
